/**
 * Form Validation Schemas
 * 
 * Defines Zod schemas for form validation across the application.
 * Provides type-safe validation with clear error messages.
 */

import { z } from 'zod'

// ====================================
// AUTHENTICATION SCHEMAS
// ====================================

/**
 * Email validation schema
 * Requirements: Valid email format
 */
const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')
  .max(254, 'Email is too long') // RFC 5321 limit

/**
 * Password validation schema (login)
 * Kept lenient — server validates credentials. Strict rules live in strongPasswordSchema (registration).
 */
const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .max(128, 'Password is too long')

/**
 * Strong password validation schema
 * Requirements: 
 * - At least 8 characters
 * - Contains uppercase letter
 * - Contains lowercase letter  
 * - Contains number
 * - Contains special character
 */
const strongPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character')

/**
 * Full name validation schema
 * Requirements: 2-100 characters, letters and common characters only
 */
const fullNameSchema = z
  .string()
  .min(2, 'Full name must be at least 2 characters')
  .max(100, 'Full name is too long')
  .regex(/^[a-zA-Z\s\-'\.]+$/, 'Full name can only contain letters, spaces, hyphens, apostrophes, and periods')

/**
 * Login form validation schema
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
})

/**
 * Registration form validation schema
 */
export const registerSchema = z.object({
  full_name: fullNameSchema,
  email: emailSchema,
  password: strongPasswordSchema,
})

/**
 * Password confirmation schema (for forms that require password confirmation)
 */
export const registerWithConfirmationSchema = z
  .object({
    full_name: fullNameSchema,
    email: emailSchema,
    password: strongPasswordSchema,
    confirmPassword: strongPasswordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })

// ====================================
// CHATBOT SCHEMAS
// ====================================

/**
 * Chatbot creation/edit validation schema
 */
export const chatbotSchema = z.object({
  name: z
    .string()
    .min(2, 'Chatbot name must be at least 2 characters')
    .max(100, 'Chatbot name is too long')
    .regex(/^[a-zA-Z0-9\s\-_]+$/, 'Chatbot name can only contain letters, numbers, spaces, hyphens, and underscores'),
  
  description: z
    .string()
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description is too long'),
  
  industry: z
    .string()
    .optional(),
  
  use_case: z
    .string()
    .max(200, 'Use case description is too long')
    .optional(),
  
  target_audience: z
    .string()
    .max(200, 'Target audience description is too long')
    .optional(),
  
  business_context: z
    .string()
    .max(1000, 'Business context is too long')
    .optional(),
  
  response_style: z
    .enum(['quick', 'detailed', 'step_by_step'])
    .optional(),
  
  technical_level: z
    .enum(['beginner', 'intermediate', 'expert'])
    .optional(),
  
  conversation_tone: z
    .enum(['professional', 'friendly', 'casual', 'technical', 'formal'])
    .optional(),
  
  personality_traits: z
    .enum(['helpful', 'direct', 'patient', 'expert', 'approachable'])
    .optional(),
  
  search_threshold: z
    .number()
    .min(0.1, 'Search threshold must be at least 0.1')
    .max(1.0, 'Search threshold must be at most 1.0')
    .optional(),
})

// ====================================
// FILE UPLOAD SCHEMAS
// ====================================

/**
 * File upload validation schema
 */
export const fileUploadSchema = z.object({
  files: z
    .array(z.instanceof(File))
    .min(1, 'At least one file is required')
    .max(50, 'Cannot upload more than 50 files at once')
    .refine(
      (files) => files.every(file => file.size <= 10 * 1024 * 1024), // 10MB
      'Each file must be smaller than 10MB'
    )
    .refine(
      (files) => files.every(file => {
        const allowedTypes = [
          'text/csv',
          'application/pdf',
          'text/plain',
          'application/json',
          'text/xml',
          'application/xml'
        ]
        return allowedTypes.includes(file.type)
      }),
      'Only CSV, PDF, TXT, JSON, and XML files are allowed'
    ),
})

// ====================================
// SEARCH/FILTER SCHEMAS
// ====================================

/**
 * Search query validation schema
 */
export const searchSchema = z.object({
  query: z
    .string()
    .min(1, 'Search query cannot be empty')
    .max(500, 'Search query is too long'),
  
  filters: z
    .object({
      dateFrom: z.date().optional(),
      dateTo: z.date().optional(),
      category: z.string().optional(),
      status: z.enum(['active', 'inactive', 'all']).optional(),
    })
    .optional(),
})

// ====================================
// TYPE EXPORTS
// ====================================

// Export inferred types for use in components
export type LoginFormData = z.infer<typeof loginSchema>
export type RegisterFormData = z.infer<typeof registerSchema>
export type RegisterWithConfirmationFormData = z.infer<typeof registerWithConfirmationSchema>
export type ChatbotFormData = z.infer<typeof chatbotSchema>
export type FileUploadFormData = z.infer<typeof fileUploadSchema>
export type SearchFormData = z.infer<typeof searchSchema>

// ====================================
// VALIDATION UTILITIES
// ====================================

/**
 * Validates data against a schema and returns formatted errors
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @returns Validation result with success flag and errors
 */
export function validateWithSchema<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): {
  success: boolean
  data?: T
  errors?: Record<string, string>
} {
  try {
    const validatedData = schema.parse(data)
    return {
      success: true,
      data: validatedData,
    }
  } catch (error) {
    if (error instanceof z.ZodError) {
      const formattedErrors: Record<string, string> = {}
      
      error.errors.forEach((err) => {
        const path = err.path.join('.')
        formattedErrors[path] = err.message
      })
      
      return {
        success: false,
        errors: formattedErrors,
      }
    }
    
    return {
      success: false,
      errors: { _general: 'Validation failed' },
    }
  }
}

/**
 * Validates a single field against its schema
 * @param schema - Zod schema for the field
 * @param value - Value to validate
 * @returns Error message if invalid, undefined if valid
 */
export function validateField<T>(
  schema: z.ZodSchema<T>,
  value: unknown
): string | undefined {
  try {
    schema.parse(value)
    return undefined
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors[0]?.message
    }
    return 'Validation failed'
  }
}

/**
 * Password strength checker
 * @param password - Password to check
 * @returns Strength score (0-5) and feedback
 */
export function checkPasswordStrength(password: string): {
  score: number
  feedback: string[]
  isStrong: boolean
} {
  const feedback: string[] = []
  let score = 0

  // Length check
  if (password.length >= 8) {
    score += 1
  } else {
    feedback.push('Use at least 8 characters')
  }

  // Uppercase letter check
  if (/[A-Z]/.test(password)) {
    score += 1
  } else {
    feedback.push('Add an uppercase letter')
  }

  // Lowercase letter check
  if (/[a-z]/.test(password)) {
    score += 1
  } else {
    feedback.push('Add a lowercase letter')
  }

  // Number check
  if (/[0-9]/.test(password)) {
    score += 1
  } else {
    feedback.push('Add a number')
  }

  // Special character check
  if (/[^A-Za-z0-9]/.test(password)) {
    score += 1
  } else {
    feedback.push('Add a special character')
  }

  return {
    score,
    feedback,
    isStrong: score >= 4,
  }
}